import Cookies from 'universal-cookie';
import { fetchOrAddWishlist } from '../../client/redux/wishlist/actions';
import { getLoggedInUserData, loginFailed } from '../../client/redux/user/actions';
import { fetchCartData, fetchCartCount } from '../../client/redux/cart/actions';
import { showOverlay, hideOverlay } from '../../client/redux/overlays/actions';
import { OverlayId as LocalOverlayId } from '../../client/components/Overlays/types';
import { BellaDispatch } from '../../client/redux/store';
import { Config } from '../types/config';
import { NEVER_SHOW_SHOP_TO_CLUB } from '../../client/hooks/flags/useNeverShowShopToClub';
import { disableShopToClub } from '../../client/redux/configurations';

export const CartFlyoutTimeoutKey = 'ww_cart_flyout_timeout';

export enum OverlayIds {
    LOGIN_AND_REGISTER = 'LOGIN_AND_REGISTER',
    ALICE_SHOP2CLUB = 'SHOP2CLUB',
}

export enum EventNames {
    UPDATE_LOGIN_STATE = 'UPDATE_LOGIN_STATE',
    UPDATE_WISHLIST = 'UPDATE_WISHLIST',
    UPDATE_CART = 'UPDATE_CART',
    UPDATE_OVERLAYS = 'UPDATE_OVERLAYS',
    SIMULATE_BRAZE_PRODUCT_RATINGS = 'SIMULATE_BRAZE_PRODUCT_RATINGS',
}

export enum GenericActions {
    REFRESH = 'REFRESH',
}

export enum UpdateLoginSateActions {
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT',
    EXISTING_USER_LOGIN_FAIL = 'EXISTING_USER_LOGIN_FAIL',
}

export enum UpdateOverlayActions {
    SHOW = 'SHOW',
    HIDE = 'HIDE',
}

export interface GenericEventDetail {
    action: GenericActions;
}

export interface UpdateOverlayDetail {
    action: UpdateOverlayActions;
    id: OverlayIds;
    options?: UpdateOverlayOptions;
}

export interface UpdateCartDetail {
    action: GenericActions;
    shouldShowCartFlyout: boolean;
}

export interface UpdateOverlayOptions {
    isLogin?: boolean;
    redirectUrl?: string;
    email?: string;
}

export interface Event {
    name: string;
    event: (e: CustomEvent) => void;
}

export const createHeaderEvents = (dispatch: BellaDispatch, config: Config, cookies: Cookies) => {
    const { currency, countryCode } = config;
    return [
        {
            name: EventNames.UPDATE_WISHLIST,
            event: (e: CustomEvent) => {
                const { action } = e.detail;
                if (action === GenericActions.REFRESH) {
                    dispatch(fetchOrAddWishlist());
                }
            },
        },
        {
            name: EventNames.UPDATE_LOGIN_STATE,
            event: (e: CustomEvent) => {
                const { action } = e.detail;
                if (action === UpdateLoginSateActions.LOGIN) {
                    dispatch(getLoggedInUserData());
                } else if (action === UpdateLoginSateActions.EXISTING_USER_LOGIN_FAIL) {
                    dispatch(loginFailed(new Error('remote client user failed to login')));
                    dispatch(disableShopToClub());
                    cookies.set(NEVER_SHOW_SHOP_TO_CLUB, 1, { path: '/' });
                }
            },
        },
        {
            name: EventNames.UPDATE_CART,
            event: async (e: CustomEvent) => {
                const { action } = e.detail;
                if (action !== GenericActions.REFRESH) {
                    return;
                }

                await Promise.all([dispatch(fetchCartCount()), dispatch(fetchCartData({ countryCode, currency }))]);
                if (!e.detail.shouldShowCartFlyout) {
                    return;
                }
                dispatch(showOverlay(LocalOverlayId.DESKTOP_CART_OVERLAY));
                const timeoutID = window.setTimeout(() => {
                    dispatch(hideOverlay(LocalOverlayId.DESKTOP_CART_OVERLAY));
                }, 2000);
                window[CartFlyoutTimeoutKey] = timeoutID;
            },
        },
    ];
};

export function EventsListener(events: Event[], el: HTMLElement | Document = document) {
    const addEvent = (evtName: string, cb: (e: CustomEvent) => void) => {
        el.addEventListener(evtName, cb, false);
    };

    const removeEvent = (evtName: string, cb: (e: CustomEvent) => void) => {
        el.removeEventListener(evtName, cb, false);
    };

    events.forEach(e => addEvent(e.name, e.event));
    return {
        dispose: () => {
            events.forEach(e => removeEvent(e.name, e.event));
        },
    };
}

export const EventDispatcher = (el: HTMLElement | Document = document) => {
    const dispatch = (event: CustomEvent) => {
        el.dispatchEvent(event);
    };

    const create = (name: EventNames, detail: object) => {
        return new CustomEvent(name, detail);
    };

    const updateOverlay = (action: UpdateOverlayActions, id: OverlayIds, options?: UpdateOverlayOptions) => {
        const detail: UpdateOverlayDetail = {
            action,
            id,
            options,
        };
        dispatch(create(EventNames.UPDATE_OVERLAYS, { detail }));
    };

    const updateCart = (action: GenericActions, shouldShowCartFlyout: boolean) => {
        const detail: UpdateCartDetail = {
            action,
            shouldShowCartFlyout,
        };
        dispatch(create(EventNames.UPDATE_CART, { detail }));
    };
    return { updateOverlay, updateCart };
};

export default EventsListener;
